// Third-party
import React from 'react';
import { Waypoint } from 'react-waypoint';
import { SortingRule } from 'react-table';
import classNames from 'classnames';

// Components
import { LoadingSpinner } from '../../components/common';
import { PortfoliosTable } from '../../components/tables';
import { SearchInput } from '../../components/inputs';
import RateLimitedComponent from '../../components/RateLimitedComponent';
import { RateLimitType } from '../../../redux/modules/types/rate-limits';
import { PrimaryButton } from '../../components/buttons';
import ConnectedPrivateComponent from '../../components/PrivateComponent';

// Utils
import { formatMessage, FormattedMessage } from '../../../utils/i18n';
import { PERMISSIONS, userHasPermissionEnabled, userHasPermissions } from '../../../utils/permissions';

// Enums
import { PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS } from '../../../enums/Portfolio';
import User from '@/types/user';
import Portfolio from '@/types/portfolio';
import DropdownButton, { DropdownButtonMenuItem } from '@/views/components/buttons/DropdownButton';
import { Typography } from '@capintel/component-lib/typography';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

type Props = {
  user: User;
  query?: string;
  searching: boolean;
  initialized: boolean;
  models: Portfolio[];
  comparingModelIds: string[];
  hasMore: boolean;
  portfoliosTableRef: React.RefObject<PortfoliosTable<Portfolio>>;
  onSearch: (query: string) => void;
  onLoadMore: () => void;
  onSort: (sorting: SortingRule[]) => void;
  onDeletePortfolio: (portfolios: Portfolio[]) => void;
  onEditPortfolio: (portfolio: Portfolio) => void;
  onCompareSelected: (portfolios: Portfolio[]) => void;
  createDropdownItems: () => DropdownButtonMenuItem[];
};

class LegacyMyModelsTab extends React.Component<Props> {
  containerRef = React.createRef<HTMLDivElement>();

  getDisplayedColumns = () => {
    const { user } = this.props;

    return [
      {
        key: PORTFOLIO_TABLE_COLUMNS.NAME,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LOCK,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.RISK_PROFILE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LAST_MODIFIED,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.SHOW_DETAILS,
        hidden: !userHasPermissionEnabled(user, PERMISSIONS.SHOW_PORTFOLIO_DETAILS),
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.COMPARE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.MENU_TOGGLE,
      },
    ]
      .filter(col => !col.hidden)
      .map(col => col.key);
  };

  renderPortfoliosSection = (title: string, portfolios: Portfolio[]) => {
    const { searching, hasMore, onLoadMore, initialized, user } = this.props;

    const displayedColumns = this.getDisplayedColumns();
    const displayedMetrics = [PORTFOLIO_METRICS_FIELDS.MER, PORTFOLIO_METRICS_FIELDS.YEAR_ONE, PORTFOLIO_METRICS_FIELDS.YEAR_THREE];
    const canCreate = userHasPermissions(user, [PERMISSIONS.USER_MODELS, PERMISSIONS.USER_MODELS_CREATE]);

    return (
      <div className="portfolios-section">
        <div className="section-headers">
          <Typography as={ALLOWED_HTML_TAGS.H2} variant="headerSm">
            {title}
          </Typography>
          <div className="section-actions">
            <SearchInput loading={searching} key="models" placeholder={formatMessage('Models.SearchMyModels')} onChange={this.props.onSearch} />
            <ConnectedPrivateComponent forcedFalse={!canCreate}>
              {() => (
                <RateLimitedComponent type={RateLimitType.MODELS}>
                  {({ disabled, error }) => (
                    <DropdownButton
                      toggle={{
                        component: PrimaryButton,
                        dark: true,
                        icon: 'plus-med',
                        className: 'create-portfolios',
                        compact: true,
                      }}
                      align="end"
                      items={this.props.createDropdownItems()}
                    >
                      <FormattedMessage id="Contacts.ContactTabs.Portfolios.Create" />
                    </DropdownButton>
                  )}
                </RateLimitedComponent>
              )}
            </ConnectedPrivateComponent>
          </div>
        </div>

        <div className="portfolios">
          {(searching || !initialized) && <LoadingSpinner />}

          <PortfoliosTable<Portfolio>
            ref={this.props.portfoliosTableRef}
            portfolios={portfolios}
            className={classNames({ 'visually-hidden': searching || !initialized })}
            displayedColumns={displayedColumns}
            displayedMetrics={displayedMetrics}
            onLoadMore={this.props.onLoadMore}
            onSort={this.props.onSort}
            onDelete={this.props.onDeletePortfolio}
            onEdit={this.props.onEditPortfolio}
            onCompare={this.props.onCompareSelected}
            selectedTitle="PortfoliosTable.SelectedTitle.Models"
            comparingPortfolioIds={this.props.comparingModelIds}
            messageIds={{
              deleteMultiple: 'PortfoliosTable.Buttons.DeleteSelectedModels',
              deleteSingle: 'PortfoliosTable.Buttons.DeleteModel',
              editSingle: 'PortfoliosTable.Buttons.EditModel',
            }}
            canEditPredicate={portfolio => !portfolio.availableActions || portfolio.availableActions.edit}
            canClonePredicate={portfolio => !portfolio.availableActions || portfolio.availableActions.clone}
            canDeletePredicate={portfolio => !portfolio.availableActions || portfolio.availableActions.delete}
            canAcceptPredicate={() => false}
          />

          {hasMore && initialized && !searching && (
            <>
              <Waypoint onEnter={onLoadMore} />
              <LoadingSpinner />
            </>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { models } = this.props;

    return (
      <div ref={this.containerRef} className="all-models">
        <ConnectedPrivateComponent permissions={[PERMISSIONS.USER_MODELS]}>
          {() => <>{this.renderPortfoliosSection(formatMessage('Models.MyModels'), models)}</>}
        </ConnectedPrivateComponent>
      </div>
    );
  }
}

export default LegacyMyModelsTab;
